<template>
  <div>
    <!-- Floating trigger button (draggable) -->
    <div
      class="pollie-fab"
      :style="fabStyle"
      @mousedown="onFabMousedown"
      :title="isOpen ? 'Close Pollie' : 'Ask Pollie'"
    >
      <span class="pollie-fab-dots">
        <span class="pollie-fab-dot pollie-fab-dot--1"></span>
        <span class="pollie-fab-dot pollie-fab-dot--2"></span>
        <span class="pollie-fab-dot pollie-fab-dot--3"></span>
      </span>
      <span v-if="unreadCount > 0" class="pollie-fab-badge">{{ unreadCount }}</span>
    </div>

    <!-- Chat panel — shown when open -->
    <transition name="pollie-panel">
      <div
        v-if="isOpen"
        class="pollie-panel"
        :style="panelStyle"
      >
        <!-- Header -->
        <div class="pollie-panel-header">
          <div class="pollie-panel-title">
            <span class="pollie-header-dots">
              <span class="pollie-header-dot pollie-header-dot--1"></span>
              <span class="pollie-header-dot pollie-header-dot--2"></span>
              <span class="pollie-header-dot pollie-header-dot--3"></span>
            </span>
            <span class="pollie-panel-title-text">
              Pollie's Perspective
              <span class="pollie-panel-subtitle">AI insights — always verify with primary sources</span>
            </span>
          </div>
          <div class="pollie-panel-actions">
            <span
              v-if="conversationTracked"
              class="pollie-header-tracked"
              title="This conversation is on IQ Hub"
            >
              <i class="fas fa-check-circle"></i> On IQ Hub
            </span>
            <button
              v-else-if="canTrackConversation"
              class="pollie-header-btn pollie-track-header-btn"
              @click="openTrackForm"
              title="Track this conversation on IQ Hub"
            >
              <i class="fas fa-satellite-dish"></i> Track
            </button>
            <button
              v-if="history.length > 0 || currentAnswer"
              class="pollie-header-btn pollie-packet-btn"
              @click="carryChatToPacketBuilder"
              title="Open Pollie Packets with this conversation"
            >
              <i class="fas fa-folder-plus"></i> Save Packet
            </button>
            <button
              v-if="history.length > 0"
              class="pollie-header-btn pollie-clear-btn"
              @click="clearHistory"
              title="Clear conversation"
            >
              <i class="fas fa-trash-alt"></i> Clear
            </button>
            <button
              class="pollie-header-btn pollie-close-btn"
              @click="isOpen = false"
              title="Minimize"
            >
              <i class="fas fa-minus"></i>
            </button>
          </div>
        </div>

        <!-- Scope: National vs state-specific answers (Hole 2) -->
        <div class="pollie-scope-row">
          <label class="pollie-scope-field-label" for="pollie-context-state">Answering for</label>
          <select
            id="pollie-context-state"
            class="pollie-scope-select"
            v-model="pollieContextState"
            @change="onPollieScopeChange"
          >
            <option value="">National / federal</option>
            <option v-for="s in US_STATES" :key="s.code" :value="s.code">{{ s.code }} — {{ s.name }}</option>
          </select>
        </div>

        <div v-if="canReport" class="pollie-mode-row" role="group" aria-label="Pollie mode">
          <button
            type="button"
            class="pollie-mode-btn"
            :class="{ 'is-on': pollieMode === 'ask' }"
            @click="pollieMode = 'ask'"
          >Ask</button>
          <button
            type="button"
            class="pollie-mode-btn"
            :class="{ 'is-on': pollieMode === 'report' }"
            @click="pollieMode = 'report'"
          >Report</button>
        </div>
        <p v-if="canReport" class="pollie-mode-caption">
          {{ pollieMode === 'report' ? pollieReportModeCaption : pollieAskModeCaption }}
        </p>
        <p v-if="canReport && pollieMode === 'report'" class="pollie-report-scope">
          {{ pollieReportContext.scopeLine }}
        </p>

        <!-- Track conversation (full thread) -->
        <div v-if="trackFormKey === 'conversation'" class="pollie-track-form pollie-track-form--panel">
          <div class="pollie-track-name-label">Name for IQ Hub <span class="pollie-track-name-hint">— whole conversation</span></div>
          <input
            v-model="trackFormLabel"
            class="pollie-track-label-input"
            placeholder="Short name for IQ Hub…"
            maxlength="60"
            @keydown.enter.prevent="confirmTrack"
            @keydown.escape="trackFormKey = null"
          />
          <div class="pollie-track-duration-chips">
            <button
              v-for="chip in durationChips" :key="chip.value"
              class="pollie-chip"
              :class="{ 'pollie-chip--active': trackFormDuration === chip.value }"
              @click="trackFormDuration = chip.value"
            >{{ chip.label }}</button>
          </div>
          <div v-if="userIsOrgAdmin" class="pollie-track-scope-row">
            <label class="pollie-scope-label">
              <input type="radio" v-model="trackFormScope" value="personal" /> Just me
            </label>
            <label class="pollie-scope-label">
              <input type="radio" v-model="trackFormScope" value="org" /> Org-wide
            </label>
          </div>
          <div class="pollie-track-form-actions">
            <button class="pollie-track-save-btn" @click="confirmTrack">
              <i class="fas fa-satellite-dish"></i> Track on IQ Hub
            </button>
            <button class="pollie-track-cancel-btn" @click="trackFormKey = null">Cancel</button>
          </div>
          <div v-if="trackFormError" class="pollie-error-msg">{{ trackFormError }}</div>
        </div>

        <!-- Conversation -->
        <div class="pollie-panel-body" ref="bodyEl">

          <!-- Empty state -->
          <div v-if="history.length === 0 && !currentAnswer && !loading" class="pollie-empty">
            <template v-if="pollieMode === 'report'">
              <p>Build a printable list from this page’s data.</p>
              <p class="pollie-empty-hint">{{ pollieReportContext.hint }}</p>
            </template>
            <template v-else>
              <p>Ask a plain English question about policy, bills, or what’s changing.</p>
              <p class="pollie-empty-hint">Pollie searches the knowledge library and live sources when needed.</p>
            </template>
          </div>

          <!-- History (all past exchanges) -->
          <div
            v-for="(item, i) in history"
            :key="i"
            class="pollie-exchange"
          >
            <div class="pollie-bubble pollie-bubble-user">{{ item.question }}</div>
            <div class="pollie-bubble pollie-bubble-answer pollie-bubble-answer--md" v-html="renderMarkdown(item.answer.answer)"></div>
            <div v-if="item.answer && item.answer.report" class="pollie-report">
              <p v-if="!item.answer.report.rows.length" class="pollie-report-empty">No cards matched.</p>
              <ul v-else class="pollie-report-list">
                <li v-for="row in item.answer.report.rows" :key="row.uid">
                  <span class="pollie-report-uid">{{ row.uid }}</span>
                  <span class="pollie-report-title">{{ row.title }}</span>
                  <span class="pollie-report-meta">{{ row.status }} · {{ row.assigned }}</span>
                </li>
              </ul>
              <button type="button" class="pollie-report-print" @click="printReport(item.answer.report)">
                <i class="fas fa-print"></i> Print
              </button>
            </div>

            <div v-if="!(item.answer && item.answer.report)" class="pollie-track-row">
              <span v-if="isOnLegislativeWatch(item)" class="pollie-track-confirm pollie-watch-confirm">
                <i class="fas fa-check-circle"></i> On Legislative Watch
              </span>
              <template v-else-if="watchFormKey === i">
                <div class="pollie-track-form pollie-watch-form">
                  <div class="pollie-track-name-label">Add to Legislative Watch <span class="pollie-track-name-hint">— association bill portfolio</span></div>
                  <input
                    v-model="watchFormBill"
                    class="pollie-track-label-input"
                    placeholder="Bill / file code (e.g. SB 738 or CMS-2454-IFC)"
                    maxlength="40"
                    @keydown.enter.prevent="confirmLegislativeWatch(item)"
                    @keydown.escape="watchFormKey = null"
                  />
                  <input
                    v-model="watchFormTitle"
                    class="pollie-track-label-input"
                    placeholder="Short title (optional)"
                    maxlength="120"
                  />
                  <div class="pollie-track-form-actions">
                    <button class="pollie-track-save-btn pollie-watch-save-btn" @click="confirmLegislativeWatch(item)">
                      <i class="fas fa-eye"></i> Add to Legislative Watch
                    </button>
                    <button class="pollie-track-cancel-btn" @click="watchFormKey = null">Cancel</button>
                  </div>
                  <div v-if="watchFormError" class="pollie-error-msg" style="margin-top:4px;">{{ watchFormError }}</div>
                </div>
              </template>
              <template v-else>
                <button class="pollie-track-btn pollie-watch-btn" @click="openWatchForm(item, i)">
                  <i class="fas fa-eye"></i> Add to Legislative Watch
                </button>
              </template>
              <span v-if="newsletterAddedKeys.has(i)" class="pollie-track-confirm pollie-newsletter-confirm">
                <i class="fas fa-check-circle"></i> On Member Newsletter
              </span>
              <button
                v-else
                class="pollie-track-btn pollie-newsletter-btn"
                :disabled="newsletterBusyKey === i"
                @click="addToNewsletter(item, i)"
              >
                <i :class="newsletterBusyKey === i ? 'fas fa-spinner fa-spin' : 'fas fa-newspaper'"></i>
                {{ newsletterBusyKey === i ? 'Adding…' : 'Add to Newsletter' }}
              </button>
            </div>

            <div class="pollie-ai-disclosure">AI-generated · Not human curated · Always verify with primary sources before operational use.</div>

            <!-- Citations — collapsed by default -->
            <div v-if="item.answer.citations && item.answer.citations.length" class="pollie-sources">
              <button
                class="pollie-sources-toggle"
                @click="toggleCitations(i)"
              >
                <i class="fas" :class="citationsOpen[i] ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                {{ item.answer.citations.length }} source{{ item.answer.citations.length !== 1 ? 's' : '' }}
              </button>
              <div v-if="citationsOpen[i]" class="pollie-sources-list">
                <div
                  v-for="cite in item.answer.citations"
                  :key="cite.id !== null ? cite.id : cite.url"
                  class="pollie-source-row pollie-source-clickable"
                  @click="cite.id !== null ? goToSignal(cite.id) : cite.url && openUrl(cite.url)"
                  :title="cite.id !== null ? 'View in Policy Engine' : cite.url"
                >
                  <i class="fas" :class="cite.id !== null ? 'fa-file-alt' : 'fa-external-link-alt'"></i>
                  <span class="pollie-source-title">{{ cite.title }}</span>
                  <span v-if="cite.id === null && cite.source" class="pollie-source-domain">{{ cite.source }}</span>
                </div>
              </div>
            </div>

            <div v-if="item.answer.live_fetch_used" class="pollie-live-tag">
              <i class="fas fa-wifi"></i>
              Live search
              <span v-if="item.answer.new_records_created > 0" class="pollie-new-records">
                · {{ item.answer.new_records_created }} source{{ item.answer.new_records_created !== 1 ? 's' : '' }} queued for corpus
                <span
                  v-for="rec in (item.answer.new_records || [])"
                  :key="rec.url || rec.id"
                  class="pollie-new-record-link"
                  @click.stop="rec.id ? goToSignal(rec.id) : openUrl(rec.url)"
                  :title="rec.url || rec.title"
                >{{ rec.title }}</span>
              </span>
            </div>
          </div>

          <!-- Current answer (most recent in-flight result) -->
          <div v-if="currentAnswer" class="pollie-exchange">
            <div class="pollie-bubble pollie-bubble-user">{{ lastQuestion }}</div>
            <div class="pollie-bubble pollie-bubble-answer pollie-bubble-answer--md" v-html="renderMarkdown(currentAnswer.answer)"></div>

            <div class="pollie-track-row">
              <span v-if="isOnLegislativeWatch({ question: lastQuestion, answer: currentAnswer })" class="pollie-track-confirm pollie-watch-confirm">
                <i class="fas fa-check-circle"></i> On Legislative Watch
              </span>
              <template v-else-if="watchFormKey === 'current'">
                <div class="pollie-track-form pollie-watch-form">
                  <div class="pollie-track-name-label">Add to Legislative Watch <span class="pollie-track-name-hint">— association bill portfolio</span></div>
                  <input
                    v-model="watchFormBill"
                    class="pollie-track-label-input"
                    placeholder="Bill / file code (e.g. SB 738 or CMS-2454-IFC)"
                    maxlength="40"
                    @keydown.enter.prevent="confirmLegislativeWatch({ question: lastQuestion, answer: currentAnswer })"
                    @keydown.escape="watchFormKey = null"
                  />
                  <input
                    v-model="watchFormTitle"
                    class="pollie-track-label-input"
                    placeholder="Short title (optional)"
                    maxlength="120"
                  />
                  <div class="pollie-track-form-actions">
                    <button class="pollie-track-save-btn pollie-watch-save-btn" @click="confirmLegislativeWatch({ question: lastQuestion, answer: currentAnswer })">
                      <i class="fas fa-eye"></i> Add to Legislative Watch
                    </button>
                    <button class="pollie-track-cancel-btn" @click="watchFormKey = null">Cancel</button>
                  </div>
                  <div v-if="watchFormError" class="pollie-error-msg" style="margin-top:4px;">{{ watchFormError }}</div>
                </div>
              </template>
              <template v-else>
                <button class="pollie-track-btn pollie-watch-btn" @click="openWatchForm({ question: lastQuestion, answer: currentAnswer }, 'current')">
                  <i class="fas fa-eye"></i> Add to Legislative Watch
                </button>
              </template>
              <span v-if="newsletterAddedKeys.has('current')" class="pollie-track-confirm pollie-newsletter-confirm">
                <i class="fas fa-check-circle"></i> On Member Newsletter
              </span>
              <button
                v-else
                class="pollie-track-btn pollie-newsletter-btn"
                :disabled="newsletterBusyKey === 'current'"
                @click="addToNewsletter({ question: lastQuestion, answer: currentAnswer }, 'current')"
              >
                <i :class="newsletterBusyKey === 'current' ? 'fas fa-spinner fa-spin' : 'fas fa-newspaper'"></i>
                {{ newsletterBusyKey === 'current' ? 'Adding…' : 'Add to Newsletter' }}
              </button>
            </div>

            <div class="pollie-ai-disclosure">AI-generated · Not human curated · Always verify with primary sources before operational use.</div>

            <div v-if="currentAnswer.citations && currentAnswer.citations.length" class="pollie-sources">
              <button
                class="pollie-sources-toggle"
                @click="toggleCitations('current')"
              >
                <i class="fas" :class="citationsOpen['current'] ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                {{ currentAnswer.citations.length }} source{{ currentAnswer.citations.length !== 1 ? 's' : '' }}
              </button>
              <div v-if="citationsOpen['current']" class="pollie-sources-list">
                <div
                  v-for="cite in currentAnswer.citations"
                  :key="cite.id !== null ? cite.id : cite.url"
                  class="pollie-source-row pollie-source-clickable"
                  @click="cite.id !== null ? goToSignal(cite.id) : cite.url && openUrl(cite.url)"
                  :title="cite.id !== null ? 'View in Policy Engine' : cite.url"
                >
                  <i class="fas" :class="cite.id !== null ? 'fa-file-alt' : 'fa-external-link-alt'"></i>
                  <span class="pollie-source-title">{{ cite.title }}</span>
                  <span v-if="cite.id === null && cite.source" class="pollie-source-domain">{{ cite.source }}</span>
                </div>
              </div>
            </div>

            <div v-if="currentAnswer.live_fetch_used" class="pollie-live-tag">
              <i class="fas fa-wifi"></i>
              Live search
              <span v-if="currentAnswer.new_records_created > 0" class="pollie-new-records">
                · {{ currentAnswer.new_records_created }} source{{ currentAnswer.new_records_created !== 1 ? 's' : '' }} queued for corpus
                <span
                  v-for="rec in (currentAnswer.new_records || [])"
                  :key="rec.url || rec.id"
                  class="pollie-new-record-link"
                  @click.stop="rec.id ? goToSignal(rec.id) : openUrl(rec.url)"
                  :title="rec.url || rec.title"
                >{{ rec.title }}</span>
              </span>
            </div>
          </div>

          <!-- Loading indicator -->
          <div v-if="loading" class="pollie-thinking">
            <span v-if="thinkingMessage" class="pollie-thinking-msg">{{ thinkingMessage }}</span>
            <span class="pollie-dot"></span>
            <span class="pollie-dot"></span>
            <span class="pollie-dot"></span>
            <button type="button" class="pollie-track-cancel-btn pollie-cancel-job-btn" @click="cancelPollieJob">
              Cancel
            </button>
          </div>

          <!-- Error -->
          <div v-if="error" class="pollie-error-msg">{{ error }}</div>
        </div>

        <div v-if="showAskModeNudge" class="pollie-ask-mode-nudge">
          <p class="pollie-ask-mode-nudge-lead">This looks like an Ask Pollie request.</p>
          <p class="pollie-ask-mode-nudge-text">
            Ask Pollie will give you a better response — switch to Ask mode?
          </p>
          <div class="pollie-ask-mode-nudge-actions">
            <button type="button" class="pollie-ask-mode-nudge-primary" @click="switchToAskFromNudge">
              Switch to Ask
            </button>
            <button type="button" class="pollie-ask-mode-nudge-secondary" @click="runReportFromNudge">
              Run report anyway
            </button>
          </div>
        </div>

        <div v-if="attachedName" class="pollie-attach-chip">
          <i class="fas fa-paperclip"></i>
          <span>{{ attachedName }}</span>
          <button type="button" class="pollie-attach-clear" :disabled="loading" @click="clearAttach">×</button>
        </div>

        <!-- Input -->
        <div class="pollie-panel-footer">
          <input
            ref="inputEl"
            v-model="question"
            type="text"
            class="pollie-input"
            :placeholder="pollieMode === 'report' ? pollieReportContext.placeholder : 'Ask about any policy or bill…'"
            @keydown.enter="submit"
            :disabled="loading"
          />
          <label class="pollie-attach-btn" :title="attachedName ? attachedName : 'Attach a document'">
            <i class="fas fa-paperclip"></i>
            <input
              type="file"
              class="pollie-attach-input"
              accept=".pdf,.doc,.docx,.txt,.md"
              :disabled="loading"
              @change="onAttachFile"
            />
          </label>
          <button
            class="pollie-submit-btn"
            @click="submit"
            :disabled="loading || (!question.trim() && !attachedName)"
          >
            <i class="fas fa-paper-plane"></i>
          </button>
        </div>

        <!-- Quick nav to Pollie Packets -->
        <div class="pollie-packets-nav">
          <button type="button" class="pollie-packets-nav-link" @click="carryChatToPacketBuilder">
            <i class="fas fa-file-alt"></i> Pollie Packets
          </button>
        </div>
      </div>
    </transition>

    <ProductUpsellPrompt
      :open="!!productUpsellMessage"
      :message="productUpsellMessage"
      @close="closeProductUpsell"
      @learn-more="onProductUpsellLearnMore"
    />
    <ProductLearnModal
      :product-key="productLearnKey"
      @close="closeProductLearn"
    />
  </div>
</template>

<script>
import { marked } from 'marked'
import api from '@/services/api'
import { postAndPoll } from '@/utils/pollCeleryJob'
import { interactiveJobErrorMessage } from '@/utils/interactiveJobErrors'
import authService from '@/services/auth'
import { assumedOrgState, activeOrgFlags } from '@/services/assumedOrg'
import { normalizeOrgStateCode } from '@/services/hubState'
import { shouldShowPolicyIqModule } from '@/utils/policyIqAccess'
import ProductLearnModal from '@/components/ProductLearnModal.vue'
import ProductUpsellPrompt from '@/components/ProductUpsellPrompt.vue'
import { resolvePollieReportContext } from '@/utils/pollieReportContext'
import { shouldSuggestAskForReport } from '@/utils/pollieReportAskNudge'

const FAB_SIZE = 52        // px — diameter of the button
const PANEL_W = 380        // px
const PANEL_H = 500        // px
const EDGE_PAD = 20        // px — min distance from window edge
const DRAG_THRESHOLD = 5   // px — movement less than this = click, not drag

function resolveInitialPollieContextState() {
  const isPlatformAdmin = ['PLATFORM_ADMIN', 'PLATFORM_ENGINEER'].includes(authService.getUserRole());
  const homeOrg = authService.getUserOrganization();
  const activeOrg = authService.getActiveOrganization();
  if (!isPlatformAdmin) {
    return normalizeOrgStateCode(activeOrg);
  }
  if (activeOrg && homeOrg && activeOrg.id !== homeOrg.id) {
    return normalizeOrgStateCode(activeOrg);
  }
  return '';
}

export default {
  name: 'PollieWidget',

  components: {
    ProductLearnModal,
    ProductUpsellPrompt,
  },

  data () {
    // Default position: top-right, sitting just over the nav header (~64px tall)
    const initX = window.innerWidth - FAB_SIZE - EDGE_PAD
    const initY = 38 // hovers over the top banner

    return {
      // Position of the FAB (left, top) in px
      fabX: initX,
      fabY: initY,

      // Drag state
      dragging: false,
      dragOffsetX: 0,
      dragOffsetY: 0,
      dragStartX: 0,
      dragStartY: 0,
      hasMoved: false,

      // Panel
      isOpen: false,

      // Chat state
      question: '',
      pollieMode: 'ask',
      attachedName: '',
      attachedText: '',
      loading: false,
      error: null,
      history: [],         // [{ question, answer }]
      currentAnswer: null, // answer object being shown while streaming completes
      lastQuestion: '',
      unreadCount: 0,
      citationsOpen: {},   // { [index | 'current']: bool } — collapsed by default
      conversationTrackedSession: false,
      trackFormKey: null,   // 'conversation' when track form is open
      trackFormLabel: '',
      trackFormDuration: '30', // '7' | '30' | '90' | 'ongoing'
      trackFormScope: 'personal',
      trackFormError: '',

      // Legislative Watch (association portfolio — distinct from IQ Hub Track)
      watchFormKey: null,
      watchFormBill: '',
      watchFormTitle: '',
      watchFormError: '',
      watchedBillKeys: new Set(), // normalized bill / file-code ids on watchlist
      watchedEventIds: new Set(), // corpus event ids linked on watchlist
      newsletterBusyKey: null,
      newsletterAddedKeys: new Set(),
      newsletterError: '',
      productUpsellMessage: '',
      productUpsellLearnKey: null,
      productLearnKey: null,

      // Thinking status
      thinkingMessage: '',
      thinkingTimer: null,
      pollieJobAbort: false,
      showAskModeNudge: false,

      // Answer scope — National ('') or 2-letter state; persisted in pollie_context_state
      pollieContextState: resolveInitialPollieContextState(),
    }
  },

  computed: {
    canTrackConversation () {
      const anchor = this.getTrackAnchor()
      const text = (anchor?.answer?.answer || '').trim()
      if (!text) return false
      const lower = text.toLowerCase()
      if (lower.startsWith("i wasn't able to synthesize")) return false
      if (lower.includes('do not contain any information')) return false
      return true
    },

    conversationTracked () {
      return this.conversationTrackedSession
    },

    showMemberNewsletter () {
      return shouldShowPolicyIqModule('piq_newsletter')
    },

    orgHasAdvocacyIq () {
      if (authService.hasPlatformAccess() && !assumedOrgState.org) return true
      const org = assumedOrgState.org || authService.getUserOrganization()
      const orgId = org && org.id
      const features = (activeOrgFlags.orgId === orgId && activeOrgFlags.features)
        ? activeOrgFlags.features
        : ((org && org.features_enabled) || {})
      return features.advocacy_iq === true
    },
    US_STATES () {
      return [
        { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
        { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
        { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'Washington D.C.' },
        { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
        { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
        { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
        { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
        { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
        { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
        { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
        { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
        { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
        { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
        { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
        { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
        { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
        { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
        { code: 'AS', name: 'American Samoa' }, { code: 'GU', name: 'Guam' },
        { code: 'MP', name: 'Northern Mariana Islands' }, { code: 'PR', name: 'Puerto Rico' },
        { code: 'VI', name: 'U.S. Virgin Islands' },
      ];
    },

    userIsOrgAdmin () {
      return authService.hasRole('ORG_ADMIN') || authService.hasRole('PLATFORM_ADMIN') || authService.hasRole('PLATFORM_ENGINEER')
    },

    canReport () {
      return ['PLATFORM_ADMIN', 'PLATFORM_ENGINEER'].includes(authService.getUserRole())
    },

    pollieReportContext () {
      return resolvePollieReportContext(this.$route, {
        hubState: this.pollieContextState || '',
      })
    },

    pollieAskModeCaption () {
      return 'Answers — what it means and what’s changing.'
    },

    pollieReportModeCaption () {
      return 'Lists — printable rows from this page. Use Ask for “why” or “how will…” questions.'
    },

    durationChips () {
      return [
        { label: '7 days',  value: '7' },
        { label: '30 days', value: '30' },
        { label: '90 days', value: '90' },
        { label: 'Ongoing', value: 'ongoing' },
      ]
    },

    fabStyle () {
      return {
        left: this.fabX + 'px',
        top:  this.fabY + 'px',
        cursor: this.dragging ? 'grabbing' : 'grab',
      }
    },

    panelStyle () {
      // Position the panel above (or below if near top) and to the left (or right if near right edge)
      const spaceAbove = this.fabY
      const spaceRight = window.innerWidth - this.fabX - FAB_SIZE

      let top, left

      if (spaceAbove >= PANEL_H + 12) {
        top = this.fabY - PANEL_H - 12
      } else {
        top = this.fabY + FAB_SIZE + 12
      }

      if (spaceRight >= PANEL_W) {
        left = this.fabX + FAB_SIZE + 8 - PANEL_W
      } else {
        left = this.fabX - PANEL_W + FAB_SIZE
      }

      // Clamp to viewport
      top  = Math.max(EDGE_PAD, Math.min(top,  window.innerHeight - PANEL_H - EDGE_PAD))
      left = Math.max(EDGE_PAD, Math.min(left, window.innerWidth  - PANEL_W - EDGE_PAD))

      return {
        top:  top  + 'px',
        left: left + 'px',
        width: PANEL_W + 'px',
        height: PANEL_H + 'px',
      }
    },
  },

  async mounted () {
    await this.loadWatchedBills()
    window.addEventListener('pollie:signal-tracked', this.onPollieSignalTracked)
    window.addEventListener('iqhub:state-changed', this.onHubStateChanged)
    window.addEventListener('pollie:open', this.onPollieOpen)
    this._stopAssumedOrgWatch = this.$watch(
      () => assumedOrgState.org?.id,
      () => {
        this.applyActiveOrgPollieScope()
        this.loadWatchedBills()
      },
    )
  },

  beforeUnmount () {
    if (this._stopAssumedOrgWatch) this._stopAssumedOrgWatch()
    window.removeEventListener('pollie:signal-tracked', this.onPollieSignalTracked)
    window.removeEventListener('iqhub:state-changed', this.onHubStateChanged)
    window.removeEventListener('pollie:open', this.onPollieOpen)
  },

  methods: {
    onPollieSignalTracked () {
      this.conversationTrackedSession = true
      this.trackFormKey = null
    },

    onPollieOpen (event) {
      const detail = event?.detail || {}
      this.isOpen = true
      this.unreadCount = 0
      if (detail.prefill) {
        this.question = String(detail.prefill)
      }
      const code = (detail.state || '').trim().toUpperCase()
      if (code && code !== this.pollieContextState) {
        this.pollieContextState = code
        localStorage.setItem('pollie_context_state', code)
      }
      this.$nextTick(() => {
        const el = this.$refs?.inputEl
        if (el && typeof el.focus === 'function') el.focus()
      })
    },

    onHubStateChanged (event) {
      const code = (event?.detail?.state || '').trim().toUpperCase()
      if (code === this.pollieContextState) return
      this.pollieContextState = code
      localStorage.setItem('pollie_context_state', code || 'NATIONAL')
    },

    applyActiveOrgPollieScope () {
      const isPlatformAdmin = ['PLATFORM_ADMIN', 'PLATFORM_ENGINEER'].includes(authService.getUserRole())
      if (isPlatformAdmin && !assumedOrgState.org) {
        if (this.pollieContextState !== '') {
          this.pollieContextState = ''
          localStorage.setItem('pollie_context_state', 'NATIONAL')
        }
        return
      }
      const code = normalizeOrgStateCode(authService.getActiveOrganization())
      if (code && code !== this.pollieContextState) {
        this.pollieContextState = code
        localStorage.setItem('pollie_context_state', code)
      }
    },

    renderMarkdown (text) {
      if (!text) return ''
      return marked.parse(text, { breaks: true, gfm: true })
    },

    onPollieScopeChange () {
      const v = this.pollieContextState || 'NATIONAL'
      localStorage.setItem('pollie_context_state', v)
    },

    // ── Drag ──────────────────────────────────────────────────────────────

    onFabMousedown (e) {
      // Only primary button
      if (e.button !== 0) return
      e.preventDefault()

      this.dragOffsetX = e.clientX - this.fabX
      this.dragOffsetY = e.clientY - this.fabY
      this.dragStartX  = e.clientX
      this.dragStartY  = e.clientY
      this.hasMoved    = false
      this.dragging    = true

      document.addEventListener('mousemove', this.onDragMove)
      document.addEventListener('mouseup',   this.onDragEnd)
    },

    onDragMove (e) {
      const dx = Math.abs(e.clientX - this.dragStartX)
      const dy = Math.abs(e.clientY - this.dragStartY)
      if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
        this.hasMoved = true
      }

      let newX = e.clientX - this.dragOffsetX
      let newY = e.clientY - this.dragOffsetY

      // Clamp to viewport
      newX = Math.max(EDGE_PAD, Math.min(newX, window.innerWidth  - FAB_SIZE - EDGE_PAD))
      newY = Math.max(EDGE_PAD, Math.min(newY, window.innerHeight - FAB_SIZE - EDGE_PAD))

      this.fabX = newX
      this.fabY = newY
    },

    onDragEnd () {
      this.dragging = false
      document.removeEventListener('mousemove', this.onDragMove)
      document.removeEventListener('mouseup',   this.onDragEnd)

      // Toggle open only if it was a click, not a drag
      if (!this.hasMoved) {
        this.isOpen = !this.isOpen
        if (this.isOpen) {
          this.unreadCount = 0
          this.$nextTick(() => this.$refs.inputEl?.focus())
        }
      }
    },

    // ── Chat ──────────────────────────────────────────────────────────────

    switchToAskFromNudge () {
      this.showAskModeNudge = false
      this.pollieMode = 'ask'
      this.$nextTick(() => this.submit())
    },

    runReportFromNudge () {
      this.showAskModeNudge = false
      this.submit({ forceReport: true })
    },

    async submit (options = {}) {
      const q = this.question.trim()
        || (this.attachedName
          ? `Please read this document (${this.attachedName}) and tell me what it is and what matters for home care and hospice.`
          : '')
      if (!q || this.loading) return

      if (
        !options.forceReport
        && this.canReport
        && this.pollieMode === 'report'
        && shouldSuggestAskForReport(q)
      ) {
        this.showAskModeNudge = true
        return
      }
      this.showAskModeNudge = false

      this.loading     = true
      this.pollieJobAbort = false
      this.error       = null
      this.lastQuestion = q
      this.question    = ''
      this.currentAnswer = null

      this.$nextTick(() => this.scrollToBottom())

      const thinkingStages = this.pollieMode === 'report'
        ? [
          { delay: 0, msg: 'Reading the cards\u2026' },
          { delay: 8000, msg: 'Building the list\u2026' },
        ]
        : [
          { delay: 0,     msg: 'Searching policy corpus\u2026' },
          { delay: 7000,  msg: 'Detecting coverage gaps\u2026' },
          { delay: 14000, msg: 'Fetching live sources\u2026' },
          { delay: 22000, msg: 'Synthesizing answer\u2026' },
          { delay: 35000, msg: 'Almost there, this one\u2019s complex\u2026' },
        ]
      const thinkingStart = Date.now()
      this.thinkingMessage = thinkingStages[0].msg
      this.thinkingTimer = setInterval(() => {
        const elapsed = Date.now() - thinkingStart
        for (let i = thinkingStages.length - 1; i >= 0; i--) {
          if (elapsed >= thinkingStages[i].delay) {
            this.thinkingMessage = thinkingStages[i].msg
            break
          }
        }
      }, 1000)

      try {
        const resp = { data: await postAndPoll(
          '/regulatory/pollie/ask/',
          {
            question: q,
            context_state: this.pollieContextState || '',
            attached_document: this.pollieMode === 'report' ? '' : (this.attachedText || ''),
            attached_name: this.pollieMode === 'report' ? '' : (this.attachedName || ''),
            mode: this.canReport && this.pollieMode === 'report' ? 'report' : 'ask',
            report_context: this.pollieReportContext.report_context,
            report_state: this.pollieReportContext.report_state || '',
            page_route: this.$route?.fullPath || this.$route?.path || '',
          },
          '/interactive-jobs/status/',
          {
            shouldAbort: () => this.pollieJobAbort,
            timeoutMs: this.pollieMode === 'report' ? 120000 : undefined,
          },
        ) }
        this.currentAnswer = resp.data
        this.$nextTick(() => this.scrollToBottom())
      } catch (e) {
        this.error = interactiveJobErrorMessage(e)
      } finally {
        clearInterval(this.thinkingTimer)
        this.thinkingTimer = null
        this.thinkingMessage = ''
        this.loading = false
        this.clearAttach()
      }
    },

    async onAttachFile (event) {
      const file = event.target.files?.[0]
      event.target.value = ''
      if (!file) return
      this.error = null
      const formData = new FormData()
      formData.append('file', file)
      try {
        const res = await api.post('/policy-alignment/extract-file/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        this.attachedName = res.data.filename || file.name
        this.attachedText = res.data.text || ''
      } catch (err) {
        this.attachedName = ''
        this.attachedText = ''
        this.error = err.response?.data?.error || 'Could not read that file.'
      }
    },

    printReport (report) {
      const esc = (value) => String(value ?? '').replace(/[&<>"]/g, (ch) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;',
      }[ch]))
      const rows = (report.rows || []).map((row) => (
        `<tr><td>${esc(row.uid)}</td><td>${esc(row.title)}</td><td>${esc(row.status)}</td>`
        + `<td>${esc(row.assigned)}</td><td>${esc(row.added)}</td><td>${esc(row.target)}</td></tr>`
      )).join('')
      const html = `<!DOCTYPE html><html><head><title>${esc(report.title || 'Card report')}</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; color: #212938; margin: 24px; }
          h1 { font-size: 20px; margin: 0 0 8px; }
          p { color: #6b7a99; font-size: 13px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { text-align: left; border-bottom: 1px solid #d9e2ef; padding: 6px 8px; vertical-align: top; }
          th { color: #6b7a99; }
        </style></head><body>
        <h1>${esc(report.title || 'Card report')}</h1>
        <p>${esc(report.question || '')}</p>
        <p>${esc(report.summary || '')}</p>
        <table><thead><tr><th>ID</th><th>Title</th><th>We Are</th><th>Assigned</th><th>Added</th><th>Target</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="6">No cards matched.</td></tr>'}</tbody></table>
        </body></html>`
      const frame = document.createElement('iframe')
      frame.setAttribute('title', 'Card report')
      frame.style.position = 'fixed'
      frame.style.right = '0'
      frame.style.bottom = '0'
      frame.style.width = '0'
      frame.style.height = '0'
      frame.style.border = '0'
      document.body.appendChild(frame)
      const doc = frame.contentWindow.document
      doc.open()
      doc.write(html)
      doc.close()
      frame.contentWindow.focus()
      frame.contentWindow.print()
      setTimeout(() => frame.remove(), 1000)
    },

    clearAttach () {
      this.attachedName = ''
      this.attachedText = ''
    },

    cancelPollieJob () {
      this.pollieJobAbort = true
      if (this.thinkingTimer) {
        clearInterval(this.thinkingTimer)
        this.thinkingTimer = null
      }
      this.thinkingMessage = ''
      this.loading = false
      this.error = null
      this.question = this.lastQuestion || this.question
    },

    clearHistory () {
      this.cancelPollieJob()
      this.history        = []
      this.currentAnswer  = null
      this.lastQuestion   = ''
      this.error          = null
      this.unreadCount    = 0
      this.citationsOpen  = {}
      this.conversationTrackedSession = false
      this.trackFormKey = null
    },

    toggleCitations (key) {
      this.citationsOpen = {
        ...this.citationsOpen,
        [key]: !this.citationsOpen[key],
      }
    },

    goToSignal (id) {
      this.$router.push({ path: '/policy-engine', query: { event: id, tab: 'artifacts' } }).catch(() => {})
      this.isOpen = false
    },

    openUrl (url) {
      if (url) window.open(url, '_blank', 'noopener,noreferrer')
    },

    scrollToBottom () {
      const el = this.$refs.bodyEl
      if (el) el.scrollTop = el.scrollHeight
    },

    deriveNodeLabel (question) {
      // Strip leading question words, punctuation, and produce a short label
      let stripped = question
        .replace(/^(what('?s| is| are)|who is|how (do|does|can)|tell me about|latest on|update on|status of|whats?|can you|explain)\s+/i, '')
        .replace(/\?$/, '')
        .trim()
      // Capitalize first letter
      stripped = stripped.charAt(0).toUpperCase() + stripped.slice(1)
      // Hard cap at 32 chars, break at last word boundary before the limit
      if (stripped.length <= 32) return stripped
      const cut = stripped.slice(0, 32).replace(/\s+\S*$/, '')
      return cut || stripped.slice(0, 32)
    },

    buildConversationTurns () {
      const turns = (this.history || []).map((h) => ({
        question: h.question,
        answer: h.answer,
      }))
      if (this.currentAnswer && this.lastQuestion) {
        turns.push({ question: this.lastQuestion, answer: this.currentAnswer })
      }
      return turns
    },

    getTrackAnchor () {
      const turns = this.buildConversationTurns()
      if (!turns.length) return null
      const last = turns[turns.length - 1]
      return {
        question: last.question,
        answer: last.answer,
        turns,
      }
    },

    openTrackForm () {
      if (this.conversationTracked || !this.canTrackConversation) return
      this.watchFormKey = null
      this.trackFormKey = 'conversation'
      const turns = this.buildConversationTurns()
      const firstQ = turns[0]?.question || this.lastQuestion || ''
      this.trackFormLabel = this.deriveNodeLabel(firstQ)
      this.trackFormDuration = '30'
      this.trackFormScope = 'personal'
      this.trackFormError = ''
      this.$nextTick(() => {
        const el = this.$el.querySelector('.pollie-track-form--panel .pollie-track-label-input')
        if (el) { el.focus(); el.select() }
      })
    },

    carryChatToPacketBuilder () {
      const history = this.history || []
      if (history.length) {
        const firstQ = history[0].question || ''
        const title = this.deriveNodeLabel(firstQ) || firstQ.slice(0, 80)
        const sections = history.map((item, i) => {
          const question = (item.question || '').trim()
          const answer = item.answer?.answer || ''
          return {
            order: i + 1,
            section_title: question.length > 80 ? question.slice(0, 77) + '…' : question,
            prompt: question,
            content: answer,
            citations: item.answer?.citations || [],
            status: answer ? 'complete' : 'pending',
          }
        })
        try {
          sessionStorage.setItem('pollie_packet_prefill', JSON.stringify({
            title,
            context_state: this.pollieContextState || '',
            sections,
          }))
        } catch (e) {
          console.error('Could not stash Pollie chat for packet builder', e)
        }
      } else {
        try { sessionStorage.removeItem('pollie_packet_prefill') } catch (e) { /* ignore */ }
      }
      this.isOpen = false
      this.$router.push({
        name: 'PolliePackets',
        query: history.length ? { from: 'chat', t: String(Date.now()) } : {},
      }).catch(() => {})
    },

    async confirmTrack () {
      if (this.conversationTracked) return
      const anchor = this.getTrackAnchor()
      if (!anchor?.answer?.answer?.trim()) {
        this.trackFormError = 'Wait for Pollie to finish answering, then track.'
        return
      }
      const turns = this.buildConversationTurns()
      const firstQ = turns[0]?.question || anchor.question || ''
      const label = (this.trackFormLabel || this.deriveNodeLabel(firstQ)).trim()
      const duration = this.trackFormDuration
      const scope = this.trackFormScope || 'personal'

      let track_until = null
      if (duration !== 'ongoing') {
        const d = new Date()
        d.setDate(d.getDate() + parseInt(duration, 10))
        track_until = d.toISOString().slice(0, 10)
      }

      try {
        const hubState = this.pollieContextState
          || (localStorage.getItem('iqhub_selected_state') || '').trim().toUpperCase()
        await api.post('/iq-hub/tracked-signals/', {
          node_label: label,
          scope,
          pollie_answer: anchor.answer,
          source_question: anchor.question,
          conversation_turns: turns.map((t) => ({
            question: t.question,
            answer: t.answer,
          })),
          track_until,
          org_state: hubState,
        })
        this.conversationTrackedSession = true
        this.trackFormKey = null
        this.trackFormError = ''
        window.dispatchEvent(new CustomEvent('pollie:signal-tracked'))
      } catch (e) {
        console.error('Pollie track failed', e)
        const apiError = e?.response?.data?.error
        this.trackFormError = apiError || 'Could not track this on IQ Hub. Please try again.'
      }
    },

    normalizeBillKey (raw) {
      const cleaned = String(raw || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase()
      const cms = cleaned.match(/^CMS(\d{3,5})(IFC|[PF])?$/)
      if (cms) return `CMS${cms[1]}${cms[2] || ''}`
      return cleaned
    },

    formatWatchIdentifier (raw) {
      const key = this.normalizeBillKey(raw)
      const cms = key.match(/^CMS(\d{3,5})(IFC|[PF])?$/)
      if (cms) return `CMS-${cms[1]}${cms[2] ? `-${cms[2]}` : ''}`
      const bill = key.match(/^(SB|HB|SR|HR|AB|A|S)(\d+)$/)
      if (bill) return `${bill[1]} ${bill[2]}`
      return String(raw || '').trim()
    },

    extractBillIdentifier (item) {
      const text = [
        item?.question || '',
        item?.answer?.answer || '',
        ...(item?.answer?.citations || []).map(c => c.title || ''),
      ].join(' ')
      // Prefer federal CMS file codes; longest match wins (CMS-1844-P over CMS-1844)
      const cmsRe = /\b(CMS[-\s]?\d{3,5}(?:[-\s]?(?:IFC|[PF]))?)\b/gi
      const cmsHits = [...text.matchAll(cmsRe)].map((m) => m[1])
      if (cmsHits.length) {
        const best = cmsHits
          .map((h) => this.formatWatchIdentifier(h))
          .sort((a, b) => b.length - a.length)[0]
        if (best) return best
      }
      const match = text.match(/\b((?:S\.?\s*B\.?|H\.?\s*B\.?|S\.?\s*R\.?|H\.?\s*R\.?|SB|HB|SR|HR|AB)\s*\.?\s*\d+)\b/i)
      if (match) return this.formatWatchIdentifier(match[1])
      // NJ-style bare A/S numbers (A5209, S4747): capital letter, 3+ digits, no space
      const njStyle = text.match(/\b([AS])-?(\d{3,5})\b/)
      return njStyle ? this.formatWatchIdentifier(`${njStyle[1]}${njStyle[2]}`) : ''
    },

    citationEventIds (item) {
      const cites = item?.answer?.citations || []
      return cites
        .map(c => (c && c.id != null ? Number(c.id) : null))
        .filter(id => id != null && !Number.isNaN(id))
    },

    citationEventId (item) {
      const cites = item?.answer?.citations || []
      const bill = this.normalizeBillKey(this.extractBillIdentifier(item))
      if (bill) {
        const matched = cites.find((c) => {
          if (!c || c.id == null) return false
          const titleKey = this.normalizeBillKey(c.title || '')
          return titleKey.includes(bill) || this.normalizeBillKey(this.formatWatchIdentifier(c.title || '')) === bill
        })
        // Also match CMS in title via extract on citation title alone
        const matchedCms = cites.find((c) => {
          if (!c || c.id == null) return false
          const fromTitle = this.normalizeBillKey(this.formatWatchIdentifier(
            (c.title || '').match(/\b(CMS[-\s]?\d{3,5}(?:[-\s]?(?:IFC|[PF]))?)\b/i)?.[1] || ''
          ))
          return fromTitle && fromTitle === bill
        })
        if (matchedCms) return Number(matchedCms.id)
        if (matched) return Number(matched.id)
      }
      const withId = cites.find(c => c && c.id != null)
      return withId ? Number(withId.id) : null
    },

    isOnLegislativeWatch (item) {
      // Prefer identifier match for THIS subject (CMS file code or bill number).
      // Do not treat "any related citation already on the list" as a hit.
      const bill = this.normalizeBillKey(this.extractBillIdentifier(item))
      if (bill) {
        return this.watchedBillKeys.has(bill)
      }
      const eventId = this.citationEventId(item)
      return !!(eventId && this.watchedEventIds.has(eventId))
    },

    async loadWatchedBills () {
      try {
        const res = await api.get('/advocacy/legislative-watch/', { params: { limit: 500 } })
        const keys = new Set()
        const eventIds = new Set()
        for (const row of (res.data?.results || [])) {
          const k = this.normalizeBillKey(row.bill_identifier)
          if (k) keys.add(k)
          if (row.event_id != null) eventIds.add(Number(row.event_id))
        }
        this.watchedBillKeys = keys
        this.watchedEventIds = eventIds
      } catch (e) {
        // Watchlist may be unavailable for some orgs — leave empty
        console.warn('Could not load Legislative Watch list', e)
      }
    },

    openProductUpsell (message, learnKey) {
      this.productUpsellMessage = message
      this.productUpsellLearnKey = learnKey
    },

    closeProductUpsell () {
      this.productUpsellMessage = ''
      this.productUpsellLearnKey = null
    },

    onProductUpsellLearnMore () {
      this.productLearnKey = this.productUpsellLearnKey
      this.closeProductUpsell()
    },

    closeProductLearn () {
      this.productLearnKey = null
    },

    openWatchForm (item, key) {
      if (!this.orgHasAdvocacyIq) {
        this.openProductUpsell(
          'Add Advocacy IQ to your IQ Platform to access Legislative Watch',
          'advocacy',
        )
        return
      }
      this.trackFormKey = null
      this.watchFormKey = key
      this.watchFormBill = this.extractBillIdentifier(item)
      this.watchFormTitle = this.deriveNodeLabel(item.question || '')
      this.watchFormError = ''
      this.$nextTick(() => {
        const el = this.$el.querySelector('.pollie-watch-form .pollie-track-label-input')
        if (el) { el.focus(); el.select() }
      })
    },

    async confirmLegislativeWatch (item) {
      if (!this.orgHasAdvocacyIq) {
        this.openProductUpsell(
          'Add Advocacy IQ to your IQ Platform to access Legislative Watch',
          'advocacy',
        )
        return
      }
      const bill = (this.watchFormBill || this.extractBillIdentifier(item) || '').trim()
      if (!bill) {
        this.watchFormError = 'Enter a bill or file code (e.g. SB 738 or CMS-2454-IFC).'
        return
      }
      const formattedId = this.formatWatchIdentifier(bill)
      const state = (this.pollieContextState
        || (localStorage.getItem('iqhub_selected_state') || '').trim().toUpperCase()
        || 'PA')
      const title = (this.watchFormTitle || this.deriveNodeLabel(item.question || '') || formattedId).trim()
      const eventId = this.citationEventId(item)
      const isFederal = /^CMS/i.test(formattedId)
      const payload = eventId
        ? {
            event_id: eventId,
            bill_identifier: formattedId,
            bill_title: title,
          }
        : {
            bill_identifier: formattedId,
            bill_title: title,
            bill_summary: (item?.answer?.answer || '').slice(0, 2000),
            state: isFederal || state === 'NATIONAL' || state === 'US' ? '' : state,
            jurisdiction: (isFederal || !state || state === 'NATIONAL' || state === 'US') ? 'federal' : 'state',
          }

      try {
        const res = await api.post('/advocacy/legislative-watch/', payload)
        const k = this.normalizeBillKey(res.data?.bill_identifier || formattedId)
        this.watchedBillKeys = new Set([...this.watchedBillKeys, k])
        const linkedEvent = res.data?.event_id != null ? Number(res.data.event_id) : eventId
        if (linkedEvent != null) {
          this.watchedEventIds = new Set([...this.watchedEventIds, Number(linkedEvent)])
        }
        this.watchFormKey = null
        this.watchFormBill = ''
        this.watchFormError = ''
        window.dispatchEvent(new CustomEvent('pollie:legislative-watch-added', { detail: { bill: formattedId, eventId: linkedEvent } }))
      } catch (e) {
        console.error('Pollie Legislative Watch failed', e)
        this.watchFormError = e?.response?.data?.error || 'Could not add to Legislative Watch. Please try again.'
      }
    },

    async addToNewsletter (item, key) {
      if (!this.showMemberNewsletter) {
        this.openProductUpsell(
          'Add Daily Briefing to your IQ Platform to access Intelligence Briefings',
          'policy',
        )
        return
      }
      if (this.newsletterAddedKeys.has(key) || this.newsletterBusyKey === key) return
      this.newsletterBusyKey = key
      this.newsletterError = ''
      try {
        const eventId = this.citationEventId(item)
        const title = (
          this.deriveNodeLabel(item.question || '')
          || (item.question || '').slice(0, 120)
          || 'Pollie Ask item'
        ).trim()
        const summary = (item?.answer?.answer || '').replace(/<[^>]+>/g, '').slice(0, 2000)
        const cites = item?.answer?.citations || []
        const firstUrl = (cites.find(c => c.url)?.url || '').trim()
        const payload = eventId
          ? { event_id: eventId }
          : { title, summary, source_url: firstUrl || undefined }
        await api.post('/briefings/newsletter-add-pick/', payload)
        this.newsletterAddedKeys = new Set([...this.newsletterAddedKeys, key])
      } catch (e) {
        console.error('Add to Newsletter failed', e)
        this.newsletterError = e?.response?.data?.error || 'Could not add to Newsletter'
        window.alert(this.newsletterError)
      } finally {
        this.newsletterBusyKey = null
      }
    },
  },

  watch: {
    pollieMode () {
      this.showAskModeNudge = false
    },
    currentAnswer (val) {
      if (val) {
        // Archive into history once answer arrives
        this.history.push({ question: this.lastQuestion, answer: val })
        this.currentAnswer = null

        // If panel is closed, show badge
        if (!this.isOpen) {
          this.unreadCount++
        }
        this.$nextTick(() => this.scrollToBottom())
      }
    },
  },
}
</script>

<style scoped>
/* ── Floating Action Button ─────────────────────────────────────────────── */
.pollie-fab {
  position: fixed;
  z-index: 9999;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 18px rgba(0, 140, 166, 0.38), 0 1px 4px rgba(33, 41, 56, 0.12);
  border: 2.5px solid rgba(0, 140, 166, 0.35);
  user-select: none;
  transition: box-shadow 0.15s, transform 0.15s;
}
.pollie-fab:hover {
  box-shadow: 0 6px 22px rgba(0, 140, 166, 0.5), 0 2px 6px rgba(33, 41, 56, 0.14);
  transform: scale(1.06);
}

/* Three-dot brand mark */
.pollie-fab-dots {
  display: flex;
  align-items: center;
  gap: 5px;
}
.pollie-fab-dot {
  display: block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  animation: pollie-fab-pulse 1.6s ease-in-out infinite;
}
.pollie-fab-dot--1 {
  background: #0059b2;
  animation-delay: 0s;
}
.pollie-fab-dot--2 {
  background: #008ca6;
  animation-delay: 0.27s;
}
.pollie-fab-dot--3 {
  background: #2db385;
  animation-delay: 0.54s;
}

@keyframes pollie-fab-pulse {
  0%, 60%, 100% {
    transform: scale(0.72);
    opacity: 0.4;
  }
  30% {
    transform: scale(1.18);
    opacity: 1;
  }
}
.pollie-fab-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #2db385;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
}

/* ── Panel ──────────────────────────────────────────────────────────────── */
.pollie-panel {
  position: fixed;
  z-index: 9998;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 40px rgba(33, 41, 56, 0.18);
  border: 1px solid #d9e2ef;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Panel transition */
.pollie-panel-enter-active,
.pollie-panel-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.pollie-panel-enter-from,
.pollie-panel-leave-to {
  opacity: 0;
  transform: scale(0.96) translateY(8px);
}

/* Header */
.pollie-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: #008ca6;
  color: #ffffff;
  flex-shrink: 0;
}
.pollie-panel-title {
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 9px;
}
.pollie-panel-title-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.pollie-panel-subtitle {
  font-size: 10px;
  font-weight: 400;
  opacity: 0.82;
  letter-spacing: 0.01em;
}
.pollie-header-dots {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.pollie-header-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  animation: pollie-fab-pulse 1.2s ease-in-out infinite;
}
.pollie-header-dot--1 { background: #ffffff; animation-delay: 0s; }
.pollie-header-dot--2 { background: rgba(255,255,255,0.7); animation-delay: 0.27s; }
.pollie-header-dot--3 { background: rgba(255,255,255,0.45); animation-delay: 0.54s; }
.pollie-panel-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
.pollie-header-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #ffffff;
  border-radius: 5px;
  padding: 4px 8px;
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: background 0.12s;
}
.pollie-header-btn:hover {
  background: rgba(255, 255, 255, 0.35);
}

/* Scope selector (Hole 2) */
.pollie-scope-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #ffffff;
  border-bottom: 1px solid #d9e2ef;
}
.pollie-scope-field-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7a99;
  flex-shrink: 0;
}
.pollie-scope-select {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  padding: 4px 8px;
  border: 1px solid #d9e2ef;
  border-radius: 5px;
  color: #212938;
  background: #ffffff;
}
.pollie-report-scope {
  margin: 0;
  padding: 8px 12px 0;
  font-size: 12px;
  color: #6b7a99;
  background: #ffffff;
}
.pollie-mode-row {
  display: flex;
  gap: 8px;
  padding: 8px 12px;
  background: #ffffff;
  border-bottom: 1px solid #d9e2ef;
}
.pollie-mode-btn {
  border: 1px solid #d9e2ef;
  background: #ffffff;
  color: #212938;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.pollie-mode-btn.is-on {
  background: #0059b2;
  border-color: #0059b2;
  color: #ffffff;
}
.pollie-report {
  margin-top: 8px;
}
.pollie-report-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 220px;
  overflow-y: auto;
}
.pollie-report-list li {
  padding: 6px 0;
  border-bottom: 1px solid #d9e2ef;
}
.pollie-report-uid {
  display: block;
  font-size: 11px;
  color: #6b7a99;
}
.pollie-report-title {
  display: block;
  font-size: 13px;
  color: #212938;
}
.pollie-report-meta {
  display: block;
  font-size: 11px;
  color: #6b7a99;
}
.pollie-report-empty {
  margin: 0 0 8px;
  font-size: 12px;
  color: #6b7a99;
}
.pollie-report-print {
  margin-top: 8px;
  border: 1px solid #0059b2;
  background: #ffffff;
  color: #0059b2;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

/* Body */
.pollie-panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Empty state */
.pollie-empty {
  text-align: center;
  padding: 30px 16px 10px;
  color: #6b7a99;
}
.pollie-empty p {
  font-size: 13px;
  margin: 0 0 6px;
}
.pollie-empty-hint {
  font-size: 12px;
  color: #b0bac9;
}

/* Exchange */
.pollie-exchange {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Bubbles */
.pollie-bubble {
  padding: 9px 12px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}
.pollie-bubble-user {
  background: #e5f9fd;
  color: #006e82;
  align-self: flex-end;
  max-width: 85%;
  border-bottom-right-radius: 3px;
}
.pollie-bubble-answer {
  background: #f4f7fb;
  color: #212938;
  align-self: flex-start;
  max-width: 95%;
  border-bottom-left-radius: 3px;
}

/* Sources — collapsible */
.pollie-sources {
  padding: 0 2px;
}
.pollie-sources-toggle {
  background: none;
  border: none;
  padding: 3px 0;
  font-size: 11px;
  color: #6b7a99;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: color 0.12s;
}
.pollie-sources-toggle:hover {
  color: #0059b2;
}
.pollie-sources-toggle i {
  font-size: 9px;
  width: 10px;
}
.pollie-sources-list {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  max-height: 160px;
  overflow-y: auto;
  padding: 4px 0 2px;
}
.pollie-source-row {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 4px 8px;
  border-radius: 5px;
  font-size: 12px;
  color: #6b7a99;
  background: #f4f7fb;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pollie-source-row i {
  flex-shrink: 0;
  font-size: 11px;
  color: #b0bac9;
}
.pollie-source-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}
.pollie-source-domain {
  flex-shrink: 0;
  font-size: 10px;
  color: #008ca6;
  opacity: 0.8;
  font-style: italic;
}
.pollie-source-clickable {
  color: #0059b2;
  background: #e8f2ff;
  cursor: pointer;
}
.pollie-source-clickable i {
  color: #0059b2;
}
.pollie-source-clickable:hover {
  background: #d4e8ff;
}

/* Live fetch tag */
.pollie-ai-disclosure {
  font-size: 10px;
  color: #b0bac9;
  padding: 3px 2px 6px;
  font-style: italic;
  letter-spacing: 0.01em;
}
.pollie-live-tag {
  font-size: 11px;
  color: #2db385;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0 2px;
  flex-wrap: wrap;
}
.pollie-new-records {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}
.pollie-new-record-link {
  background: #ecfaf5;
  border: 1px solid #2db385;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 10px;
  color: #1a8a62;
  cursor: pointer;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
}
.pollie-new-record-link:hover {
  background: #d4f5e9;
}

/* Thinking dots */
.pollie-thinking {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px;
  flex-wrap: wrap;
}
.pollie-cancel-job-btn {
  margin-left: auto;
}
.pollie-thinking-msg {
  font-size: 11px;
  color: #6b7a99;
  width: 100%;
  margin-bottom: 2px;
  font-style: italic;
}
.pollie-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #008ca6;
  animation: pollie-bounce 1.2s infinite ease-in-out;
}
.pollie-dot:nth-child(2) { animation-delay: 0.2s; }
.pollie-dot:nth-child(3) { animation-delay: 0.4s; }
@keyframes pollie-bounce {
  0%, 80%, 100% { transform: scale(0.75); opacity: 0.5; }
  40%            { transform: scale(1);    opacity: 1;   }
}

/* Track in IQ Hub */
.pollie-track-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 2px 2px 4px;
  flex-wrap: wrap;
}
.pollie-track-btn {
  background: none;
  border: 1px solid #d9e2ef;
  border-radius: 5px;
  padding: 3px 9px;
  font-size: 11px;
  color: #6b7a99;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.pollie-track-btn i {
  font-size: 10px;
}
.pollie-track-btn:hover {
  background: #e0efff;
  border-color: #0059b2;
  color: #0059b2;
}
.pollie-track-btn--prominent {
  background: #e5f9fd;
  border-color: #008ca6;
  color: #006e82;
  font-weight: 600;
  padding: 5px 12px;
  font-size: 12px;
  border-radius: 6px;
}
.pollie-track-btn--prominent i {
  font-size: 11px;
  color: #008ca6;
}
.pollie-track-btn--prominent:hover {
  background: #ccf2f8;
  border-color: #0073e6;
  color: #0059b2;
}
.pollie-track-confirm {
  font-size: 11px;
  color: #2db385;
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
}
.pollie-track-confirm i {
  font-size: 11px;
}
.pollie-watch-btn {
  border-color: #008ca6;
  color: #008ca6;
}
.pollie-watch-btn:hover {
  background: #e5f9fd;
  border-color: #008ca6;
  color: #006b80;
}
.pollie-newsletter-btn {
  border-color: #0059b2;
  color: #0059b2;
}
.pollie-newsletter-btn:hover {
  background: #e0efff;
  border-color: #0059b2;
  color: #00448a;
}
.pollie-newsletter-confirm {
  color: #0059b2;
}
.pollie-watch-confirm {
  color: #008ca6;
}
.pollie-watch-form {
  width: 100%;
}
.pollie-watch-save-btn {
  background: #008ca6 !important;
}
.pollie-watch-save-btn:hover {
  background: #006b80 !important;
}

/* Inline track mini-form */
.pollie-header-tracked {
  font-size: 12px;
  color: #008ca6;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-right: 6px;
}
.pollie-track-header-btn {
  color: #0059b2;
}
.pollie-track-form--panel {
  margin: 0 12px 8px;
  padding: 10px 12px;
  border: 1px solid #d9e2ef;
  border-radius: 8px;
  background: #ffffff;
}
.pollie-track-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #f7faff;
  border: 1px solid #d9e2ef;
  border-radius: 7px;
  padding: 8px 10px;
  margin-top: 2px;
}
.pollie-track-name-label {
  font-size: 11px;
  font-weight: 700;
  color: #212938;
}
.pollie-track-name-hint {
  font-weight: 400;
  color: #6b7a99;
}
.pollie-track-label-input {
  width: 100%;
  border: 1px solid #d9e2ef;
  border-radius: 5px;
  padding: 4px 8px;
  font-size: 12px;
  color: #212938;
  background: #fff;
  outline: none;
  box-sizing: border-box;
}
.pollie-track-label-input:focus {
  border-color: #0059b2;
}
.pollie-track-duration-chips {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.pollie-chip {
  background: #fff;
  border: 1px solid #d9e2ef;
  border-radius: 12px;
  padding: 2px 9px;
  font-size: 10px;
  color: #6b7a99;
  cursor: pointer;
  transition: background 0.1s, border-color 0.1s, color 0.1s;
}
.pollie-chip:hover {
  background: #e0efff;
  border-color: #0059b2;
  color: #0059b2;
}
.pollie-chip--active {
  background: #e0efff;
  border-color: #0059b2;
  color: #0059b2;
  font-weight: 600;
}
.pollie-track-scope-row {
  display: flex;
  gap: 12px;
  font-size: 11px;
  color: #6b7a99;
}
.pollie-scope-label {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}
.pollie-track-form-actions {
  display: flex;
  gap: 6px;
  align-items: center;
}
.pollie-track-save-btn {
  background: #0059b2;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 4px 12px;
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: background 0.12s;
}
.pollie-track-save-btn:hover {
  background: #0073e6;
}
.pollie-track-cancel-btn {
  background: none;
  border: none;
  font-size: 11px;
  color: #6b7a99;
  cursor: pointer;
  padding: 4px 6px;
}
.pollie-track-cancel-btn:hover {
  color: #212938;
}

/* Save as Packet button */
.pollie-packet-btn {
  background: rgba(45, 179, 133, 0.18);
}
.pollie-packet-btn:hover {
  background: rgba(45, 179, 133, 0.35);
}

/* Save as Packet mini-form */
.pollie-packet-form {
  display: flex;
  flex-direction: column;
  gap: 7px;
  background: #f0faf6;
  border: 1px solid #2db385;
  border-radius: 8px;
  padding: 10px 12px;
}
.pollie-packet-form-label {
  font-size: 11px;
  font-weight: 700;
  color: #1a8a62;
  display: flex;
  align-items: center;
  gap: 5px;
}

/* Error */
.pollie-ask-mode-nudge {
  margin: 0 12px 8px;
  padding: 10px 12px;
  background: #e0efff;
  border: 1px solid #d9e2ef;
  border-radius: 8px;
}
.pollie-ask-mode-nudge-lead {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 600;
  color: #212938;
}
.pollie-ask-mode-nudge-text {
  margin: 0 0 8px;
  font-size: 12px;
  line-height: 1.45;
  color: #6b7a99;
}
.pollie-mode-caption {
  margin: 0 12px 6px;
  font-size: 11px;
  line-height: 1.4;
  color: #6b7a99;
}
.pollie-ask-mode-nudge-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.pollie-ask-mode-nudge-primary {
  background: #0059b2;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
}
.pollie-ask-mode-nudge-primary:hover {
  background: #0073e6;
}
.pollie-ask-mode-nudge-secondary {
  background: #fff;
  color: #6b7a99;
  border: 1px solid #d9e2ef;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
}
.pollie-error-msg {
  font-size: 12px;
  color: #c0392b;
  background: #fff1f1;
  border: 1px solid #f5c6c6;
  border-radius: 6px;
  padding: 8px 10px;
}

.pollie-bubble-answer--md {
  white-space: normal;
}
.pollie-bubble-answer--md ::v-deep p {
  margin: 0 0 6px;
}
.pollie-bubble-answer--md ::v-deep p:last-child {
  margin-bottom: 0;
}
.pollie-bubble-answer--md ::v-deep strong {
  font-weight: 700;
  color: #212938;
}
.pollie-bubble-answer--md ::v-deep ul,
.pollie-bubble-answer--md ::v-deep ol {
  margin: 4px 0 6px;
  padding-left: 16px;
}
.pollie-bubble-answer--md ::v-deep li {
  margin-bottom: 2px;
}
.pollie-bubble-answer--md ::v-deep table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  margin: 6px 0;
}
.pollie-bubble-answer--md ::v-deep th,
.pollie-bubble-answer--md ::v-deep td {
  border: 1px solid #d9e2ef;
  padding: 4px 7px;
  text-align: left;
  vertical-align: top;
  line-height: 1.4;
}
.pollie-bubble-answer--md ::v-deep th {
  background: #e0efff;
  font-weight: 700;
  color: #0059b2;
}
.pollie-bubble-answer--md ::v-deep tr:nth-child(even) td {
  background: #f7faff;
}
.pollie-bubble-answer--md ::v-deep h1,
.pollie-bubble-answer--md ::v-deep h2,
.pollie-bubble-answer--md ::v-deep h3 {
  font-size: 13px;
  font-weight: 700;
  margin: 6px 0 3px;
  color: #212938;
}
.pollie-bubble-answer--md ::v-deep code {
  background: #f0f4fb;
  border-radius: 3px;
  padding: 1px 4px;
  font-size: 11px;
  font-family: monospace;
}

/* Footer / input */
.pollie-packets-nav {
  display: flex; justify-content: center;
  padding: 6px 16px 10px;
  border-top: 1px solid #f0f4fa;
}
.pollie-packets-nav-link {
  font-size: 11px; color: #6b7a99; text-decoration: none;
  display: flex; align-items: center; gap: 5px;
  transition: color 0.15s;
  background: none; border: none; padding: 0; cursor: pointer; font-family: inherit;
}
.pollie-packets-nav-link:hover { color: #0059b2; }
.pollie-panel-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid #d9e2ef;
  flex-shrink: 0;
}
.pollie-input {
  flex: 1;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d9e2ef;
  border-radius: 6px;
  font-size: 13px;
  color: #212938;
  outline: none;
  transition: border-color 0.15s;
}
.pollie-input:focus {
  border-color: #008ca6;
  box-shadow: 0 0 0 3px rgba(0, 140, 166, 0.12);
}
.pollie-input:disabled {
  background: #f8fbff;
  color: #b0bac9;
}
.pollie-attach-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  font-size: 12px;
  color: #0059b2;
  background: #e0efff;
  border-top: 1px solid #d9e2ef;
}
.pollie-attach-chip span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pollie-attach-clear {
  border: none;
  background: none;
  color: #6b7a99;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
}
.pollie-attach-btn {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid #d9e2ef;
  background: #ffffff;
  color: #0059b2;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}
.pollie-attach-btn:hover { border-color: #0059b2; }
.pollie-attach-input { display: none; }
.pollie-submit-btn {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: #008ca6;
  color: #ffffff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}
.pollie-submit-btn:hover:not(:disabled) {
  background: #0073e6;
}
.pollie-submit-btn:disabled {
  background: #b0bac9;
  cursor: not-allowed;
}
</style>
